import React, { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import IconsColor from "../../util/iconsColor"
import { getPageWindow, LIST_PAGE_SIZES, ListPageSize } from "./listPagination"
import styles from "./tablePagination.module.scss"

export interface TablePaginationBarProps {
  total: number
  page: number
  pageSize: ListPageSize
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: ListPageSize) => void
}

/** Nav order today: select, Previous, pages, Next.
 *  First (left of Previous) and Last (right of Next) can be added here later
 *  without changing list pages — same 32px height, disabled on first/last page. */

const TablePaginationBar: React.FC<TablePaginationBarProps> = ({
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) => {
  const [selectOpen, setSelectOpen] = useState(false)
  const selectButtonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLUListElement>(null)
  const [menuPos, setMenuPos] = useState({ left: 0, bottom: 0, width: 56 })

  const pageCount = total > 0 ? Math.ceil(total / pageSize) : 0
  const windowModel = getPageWindow(page, pageCount)
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, total)
  const prevDisabled = page <= 1
  const nextDisabled = pageCount <= 0 || page >= pageCount

  useEffect(() => {
    if (!selectOpen) return
    const button = selectButtonRef.current
    if (button) {
      const rect = button.getBoundingClientRect()
      setMenuPos({
        left: rect.left,
        bottom: window.innerHeight - rect.top + 4,
        width: Math.max(rect.width, 56),
      })
    }
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node
      if (selectButtonRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      setSelectOpen(false)
    }
    document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [selectOpen])

  if (total <= 0) return null

  return (
    <div className={styles.bar}>
      <span className={styles.showing}>
        Showing {from}–{to} of {total} entries
      </span>
      <div className={styles.controls}>
        <div className={styles.selectWrap}>
          <button
            ref={selectButtonRef}
            type="button"
            className={styles.selectButton}
            aria-haspopup="listbox"
            aria-expanded={selectOpen}
            aria-label="Rows per page"
            onClick={() => setSelectOpen((open) => !open)}
          >
            <span>{pageSize}</span>
            <span className={styles.selectChevron}>
              <IconsColor
                iconName="chevronDown"
                width="14px"
                height="14px"
                fillColor="none"
                strokeColor="currentColor"
              />
            </span>
          </button>
          {selectOpen &&
            createPortal(
              <ul
                ref={menuRef}
                className={styles.selectMenu}
                role="listbox"
                style={{
                  left: menuPos.left,
                  bottom: menuPos.bottom,
                  width: menuPos.width,
                }}
              >
                {LIST_PAGE_SIZES.map((size) => (
                  <li
                    key={size}
                    role="option"
                    aria-selected={size === pageSize}
                    className={`${styles.selectOption} ${size === pageSize ? styles.selectOptionActive : ""}`}
                    onClick={() => {
                      onPageSizeChange(size)
                      setSelectOpen(false)
                    }}
                  >
                    {size}
                  </li>
                ))}
              </ul>,
              document.body,
            )}
        </div>
        <button
          type="button"
          className={`${styles.navButton} ${styles.prevButton} ${prevDisabled ? styles.disabled : ""}`}
          disabled={prevDisabled}
          onClick={() => {
            if (!prevDisabled) onPageChange(page - 1)
          }}
        >
          Previous
        </button>
        {windowModel.showLeftEllipsis && <span className={styles.ellipsis}>...</span>}
        {windowModel.pages.map((pageNumber) => {
          const isActive = pageNumber === page
          const wide = pageNumber >= 10
          return (
            <button
              key={pageNumber}
              type="button"
              className={`${styles.pageButton} ${wide ? styles.pageButtonWide : ""} ${isActive ? styles.pageButtonActive : ""}`}
              aria-current={isActive ? "page" : undefined}
              onClick={() => {
                if (!isActive) onPageChange(pageNumber)
              }}
            >
              {pageNumber}
            </button>
          )
        })}
        {windowModel.showRightEllipsis && <span className={styles.ellipsis}>...</span>}
        <button
          type="button"
          className={`${styles.navButton} ${styles.nextButton} ${nextDisabled ? styles.disabled : ""}`}
          disabled={nextDisabled}
          onClick={() => {
            if (!nextDisabled) onPageChange(page + 1)
          }}
        >
          Next
        </button>
      </div>
    </div>
  )
}

export default TablePaginationBar
